import { ref, type Ref, type App } from "vue";
import type {
  AuthService,
  AuthState,
  AuthUser,
  Auth0Config,
} from "./authService";
import type { RouteLocationNormalized } from "vue-router";

export class StubAuth0Adapter implements AuthService {
  private isAuthenticated: Ref<boolean>;
  private isLoading: Ref<boolean>;
  private user: Ref<AuthUser | undefined>;
  private error: Ref<Error | undefined>;
  private auth0Config: Auth0Config;
  private accessToken: string | undefined;
  private unguardedPaths: string[];
  private getUserInfoPromise: Promise<void> | null = null;

  constructor({ auth0Config }: { auth0Config: Auth0Config }) {
    this.auth0Config = auth0Config;
    this.unguardedPaths = [auth0Config.callbackUrl || "/callback"];
    this.isAuthenticated = ref(false);
    this.isLoading = ref(false);
    this.user = ref(undefined);
    this.error = ref(undefined);
    this.accessToken = undefined;

    this.initializeAuth();
  }

  private async initializeAuth(): Promise<void> {
    this.isLoading.value = true;

    try {
      const storedToken = localStorage.getItem("minikube_access_token");

      if (storedToken) {
        this.accessToken = storedToken;
        const isExpired = this.isTokenExpired();

        if (isExpired) {
          console.warn("Stored token is expired, clearing session");
          localStorage.removeItem("minikube_access_token");
          this.accessToken = undefined;
          this.isAuthenticated.value = false;
          this.user.value = undefined;
        } else {
          await this.getUserInfo();
        }
      } else {
        this.isAuthenticated.value = false;
        this.user.value = undefined;
      }

      if (window.location.search.includes("code=")) {
        await this.handleCallback();
      }
    } catch (error) {
      console.error("Failed to initialize auth:", error);
      this.error.value = error as Error;
      this.isAuthenticated.value = false;
      this.user.value = undefined;
    } finally {
      this.isLoading.value = false;
    }
  }

  async getAccessToken(): Promise<string | undefined> {
    if (this.accessToken && this.isTokenExpired()) {
      await this.logout();
      return undefined;
    }
    return this.accessToken;
  }

  private isTokenExpired(): boolean {
    if (!this.accessToken) return true;

    try {
      const parts = this.accessToken.split(".");
      if (parts.length !== 3) {
        return true;
      }

      const base64UrlPayload = parts[1];
      const base64Payload = base64UrlPayload
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(
          base64UrlPayload.length + ((4 - (base64UrlPayload.length % 4)) % 4),
          "=",
        );
      const payload = JSON.parse(atob(base64Payload));

      if (!payload.exp) {
        return false;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }

  async login(returnTo?: string): Promise<void> {
    const authUrl = new URL(`https://${this.auth0Config.domain}/authorize`);
    const callbackUrl = `${window.location.origin}${this.auth0Config.callbackUrl || "/callback"}`;

    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("client_id", this.auth0Config.clientId);
    authUrl.searchParams.set("redirect_uri", callbackUrl);
    authUrl.searchParams.set("scope", "openid profile email");

    if (this.auth0Config.audience) {
      authUrl.searchParams.set("audience", this.auth0Config.audience);
    }

    if (returnTo) {
      authUrl.searchParams.set("state", btoa(JSON.stringify({ returnTo })));
    }

    window.location.href = authUrl.toString();
  }

  async logout(returnTo?: string): Promise<void> {
    localStorage.removeItem("minikube_access_token");
    localStorage.clear();
    this.accessToken = undefined;
    this.isAuthenticated.value = false;
    this.user.value = undefined;
    this.error.value = undefined;

    try {
      const logoutUrl = new URL(`https://${this.auth0Config.domain}/v2/logout`);
      const redirectTo = returnTo
        ? `${window.location.origin}${returnTo}`
        : window.location.origin;
      logoutUrl.searchParams.set("returnTo", redirectTo);
      window.location.href = logoutUrl.toString();
    } catch {
      const redirectTo = returnTo
        ? `${window.location.origin}${returnTo}`
        : window.location.origin;
      window.location.replace(redirectTo);
    }
  }

  getState(): AuthState {
    return {
      isAuthenticated: this.isAuthenticated,
      isLoading: this.isLoading,
      user: this.user,
      error: this.error,
    };
  }

  shouldGuardRoute(path: string): boolean {
    return !this.unguardedPaths.includes(path);
  }

  async handleCallback(url?: string): Promise<void> {
    const urlToProcess = url || window.location.href;
    const urlObj = new URL(urlToProcess);
    const code = urlObj.searchParams.get("code");
    const state = urlObj.searchParams.get("state");

    if (!code) {
      throw new Error("No authorization code found in callback");
    }

    this.isLoading.value = true;

    try {
      const tokenResponse = await fetch(
        `https://${this.auth0Config.domain}/oauth/token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
          body: new URLSearchParams({
            grant_type: "authorization_code",
            client_id: this.auth0Config.clientId,
            code: code,
            redirect_uri: `${window.location.origin}${this.auth0Config.callbackUrl || "/callback"}`,
          }),
        },
      );

      if (!tokenResponse.ok) {
        throw new Error(`Token exchange failed: ${tokenResponse.status}`);
      }

      const tokenData = await tokenResponse.json();
      this.accessToken = tokenData.access_token;

      if (this.accessToken) {
        localStorage.setItem("minikube_access_token", this.accessToken);
      }

      await this.getUserInfo();

      let returnTo = "/";
      if (state) {
        try {
          const stateData = JSON.parse(atob(state));
          returnTo = stateData.returnTo || "/";
        } catch {}
      }

      window.history.replaceState({}, "", returnTo);
    } catch (error) {
      console.error("Callback handling failed:", error);
      this.error.value = error as Error;
    } finally {
      this.isLoading.value = false;
    }
  }

  private async getUserInfo(): Promise<void> {
    if (!this.accessToken) return;

    if (this.getUserInfoPromise) {
      return this.getUserInfoPromise;
    }

    this.getUserInfoPromise = this._getUserInfo();
    try {
      await this.getUserInfoPromise;
    } finally {
      this.getUserInfoPromise = null;
    }
  }

  private async _getUserInfo(): Promise<void> {
    try {
      const userInfoResponse = await fetch(
        `https://${this.auth0Config.domain}/api/v2/users?q=user_id:"${this.getSubFromToken()}"`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            Accept: "application/json",
          },
        },
      );

      if (!userInfoResponse.ok) {
        console.error(
          `Failed to get user info: ${userInfoResponse.status} ${userInfoResponse.statusText}`,
        );
        this.isAuthenticated.value = false;
        this.user.value = undefined;
        this.error.value = new Error(
          `Failed to get user info: ${userInfoResponse.status}`,
        );
        return;
      }

      const users = await userInfoResponse.json();
      if (!users || users.length === 0) {
        console.error("No user found in API response");
        this.isAuthenticated.value = false;
        this.user.value = undefined;
        this.error.value = new Error("User not found");
        return;
      }

      const userInfo = users[0];
      this.user.value = {
        email: userInfo.email || userInfo.userEmail || "user@stub.local",
        name: userInfo.username || userInfo.name || "Stub User",
        picture: userInfo.picture || "https://via.placeholder.com/150",
        sub: userInfo.user_id || userInfo.userId || "stub_user",
      };
      localStorage.setItem("stub_auth_user", JSON.stringify(this.user.value));
      this.isAuthenticated.value = true;
    } catch (error) {
      console.error("Failed to get user info:", error);
      this.isAuthenticated.value = false;
      this.user.value = undefined;
      this.error.value = error as Error;
    }
  }

  private getSubFromToken(): string {
    if (!this.accessToken) return "stub_user";

    try {
      const base64UrlPayload = this.accessToken.split(".")[1];
      const base64Payload = base64UrlPayload
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(
          base64UrlPayload.length + ((4 - (base64UrlPayload.length % 4)) % 4),
          "=",
        );
      const payload = JSON.parse(atob(base64Payload));
      return payload.sub || "stub_user";
    } catch (error) {
      console.warn("Failed to extract sub from access token:", error);
      return "stub_user";
    }
  }

  async checkSession(): Promise<void> {
    if (this.accessToken) {
      await this.getUserInfo();
    }
  }

  async authGuard(to: RouteLocationNormalized): Promise<boolean> {
    if (!this.shouldGuardRoute(to.path)) {
      return true;
    }

    // Check if token is still valid - this will trigger logout if expired
    const token = await this.getAccessToken();
    const authState = this.getState();

    if (!token || !authState.isAuthenticated.value) {
      this.login(to.fullPath);
      return false;
    }
    return true;
  }

  install(app: App): void {
    app.config.globalProperties.$auth = this.getState();
  }

  requiresRealAuthEndpoints(): boolean {
    return true;
  }
}
